import type { SettingView, WriteSetting } from '@myrewards/contracts';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { isApiError } from '../api';
import {
  settingQuery,
  settingsAccessQuery,
  spotWorkflowSetupQuery,
  writeSetting,
} from '../api/settings';
import { InlineLinkButton, StatusPill, cx, formatDate, useScrollRegion, useToast } from '../ui';
import s from './Settings.module.css';

/** The Spot settings stored as one workflow (APP-123, APP-220). */
const SPOT_WORKFLOW_KEYS = ['spot.workflow', 'spot.cashRange', 'spot.committee'] as const;

/**
 * APP-123, APP-220: these editors share one stored workflow. Once one of them changes it (or
 * APP-222's reset does), discard the other forms' cached initial values so returning to one cannot
 * restore an old roster, old limits or an unconfigured draft; who reviews today changes with it.
 *
 * APP-268: the committee and cash limits cards now share a page, so saving one must not lose what
 * someone is still changing in the other. A form on screen keeps its draft and reads the new saved
 * copy under it; `SettingForm` then decides, by whether its own value moved, if that draft can still
 * be saved (SENT-236). This waits for that read, so a reset, which starts its forms again by key,
 * starts them from what's saved now.
 */
export async function refreshSpotWorkflow(
  queryClient: QueryClient,
  changed: string,
): Promise<void> {
  for (const sibling of SPOT_WORKFLOW_KEYS.filter((k) => k !== changed)) {
    const { queryKey } = settingQuery(sibling, 'system');
    queryClient.removeQueries({ queryKey, exact: true, type: 'inactive' });
    await queryClient.invalidateQueries({ queryKey, exact: true });
    await queryClient.invalidateQueries({ queryKey: ['settings', 'history', sibling] });
  }
  await queryClient.invalidateQueries({ queryKey: spotWorkflowSetupQuery.queryKey });
}

/** A setting as a card reads it: still loading, not open to this person, failed, or ready. */
export type SettingState =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'error' }
  | { status: 'ready'; view: SettingView };

/**
 * Whether this person may read a setting (QA-025): `undefined` while the list loads. If the list
 * can't be had, `true`: the setting's own read still refuses what isn't theirs.
 */
export function useMayRead(key: string, enabled = true): boolean | undefined {
  const access = useQuery({ ...settingsAccessQuery, enabled });
  return access.isError ? true : access.data?.readable.includes(key);
}

/** The filtered template list has its own read capability, including current Spot Committee members. */
export function useMayReadLetterTemplates(): boolean | undefined {
  const access = useQuery(settingsAccessQuery);
  return access.isError
    ? true
    : (access.data?.letterTemplatesReadable ?? access.data?.readable.includes('letters.templates'));
}

export function useSetting(key: string, scope = 'system', enabled = true): SettingState {
  // Ask only for settings this person may read (QA-025).
  const readable = useMayRead(key, enabled);
  const q = useQuery({ ...settingQuery(key, scope), enabled: enabled && readable === true });
  if (readable === false) return { status: 'hidden' };
  if (q.data !== undefined) return { status: 'ready', view: q.data };
  if (q.isError)
    return isApiError(q.error) && (q.error.status === 404 || q.error.status === 403)
      ? { status: 'hidden' }
      : { status: 'error' };
  return { status: 'loading' };
}

/** Plain words for why a change didn't go through. */
export function problemText(error: unknown): string {
  if (isApiError(error)) {
    if (error.code === 'network_error')
      return 'We couldn’t reach MyRewards. Nothing was changed; try again in a moment.';
    if (error.code === 'preview_read_only')
      return 'Preview is read-only. Exit preview to change it.';
    if (error.status === 403) return 'Your roles don’t let you change this.';
    if (error.status === 409 && error.code === 'conflict')
      return 'Someone changed this while you were working. Reload to see the latest.';
    return error.problem?.detail ?? error.problem?.title ?? error.message;
  }
  return 'That didn’t go through. Nothing was changed; try again in a moment.';
}

/**
 * One setting write and what it refreshes (PRD §17): saved on the server, audited there, then the
 * card's view and its History. A write for more scopes (R73) refreshes each. Throws a refusal
 * after refreshing what it can, so each caller words it its own way (`useSettingWrite`, the step's
 * `useSaveFor`).
 */
export function useWriteSetting(): (body: WriteSetting) => Promise<SettingView> {
  const queryClient = useQueryClient();
  return useCallback(
    async (body: WriteSetting) => {
      const scopes = [body.scope, ...(body.alsoScopes ?? []).map((a) => a.scope)];
      let view: SettingView;
      try {
        view = await writeSetting(body);
      } catch (err) {
        // Someone changed it meanwhile (SENT-041): the card shows what's saved now.
        if (isApiError(err) && err.code === 'conflict') {
          for (const scope of scopes)
            await queryClient.invalidateQueries({
              queryKey: settingQuery(body.key, scope).queryKey,
            });
          // SENT-238: and the rows and sibling cards from the same stored workflow, or someone they
          // just added reads as unknown and no longer active.
          if ((SPOT_WORKFLOW_KEYS as readonly string[]).includes(body.key))
            await refreshSpotWorkflow(queryClient, body.key);
        }
        throw err;
      }
      queryClient.setQueryData(settingQuery(body.key, body.scope).queryKey, view);
      // The other scopes the same change set: the server answers with the first one's view.
      for (const scope of scopes.slice(1))
        await queryClient.invalidateQueries({ queryKey: settingQuery(body.key, scope).queryKey });
      await queryClient.invalidateQueries({ queryKey: ['settings', 'history', body.key] });
      if ((SPOT_WORKFLOW_KEYS as readonly string[]).includes(body.key))
        await refreshSpotWorkflow(queryClient, body.key);
      return view;
    },
    [queryClient],
  );
}

/**
 * APP-273: said in the confirm dialog when someone changed the setting meanwhile. `useWriteSetting`
 * has already refreshed the card behind it, so Cancel is the way on: a reload would lose what they
 * were changing, and saving again sends the same old copy.
 *
 * APP-276: the words promise nothing about what the box holds after Cancel. Settings that share a
 * version (the Spot Committee and the cash limits) keep a draft whose own value didn’t move.
 */
const CHANGED_MEANWHILE =
  'Someone else saved a change while you were working, so yours wasn’t saved. Cancel, check what’s there now, and try again if you still need it.';

/**
 * Saves a setting: audited on the server, then the card and its History refresh. Resolves to
 * whether it went through, and keeps the reason it didn't for the confirm dialog.
 */
export function useSettingWrite(): {
  save: (body: WriteSetting, saved?: string) => Promise<boolean>;
  error: string | null;
  clearError: () => void;
} {
  const write = useWriteSetting();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const save = useCallback(
    async (body: WriteSetting, saved = 'Saved') => {
      setError(null);
      try {
        await write(body);
      } catch (err) {
        setError(isApiError(err) && err.code === 'conflict' ? CHANGED_MEANWHILE : problemText(err));
        return false;
      }
      toast.show({ tone: 'done', title: saved, body: 'It’s in the history with your name.' });
      return true;
    },
    [write, toast],
  );
  return {
    save,
    error,
    clearError: useCallback(() => {
      setError(null);
    }, []),
  };
}

/**
 * "Last changed by Diane Holloway on Jan 6, 2026 · History" under a value (PRD §17); "… by Meredith
 * Okonkwo for Elena Brightwater on Oct 23, 2026 (Terrestrial top-up)" for a change made for someone
 * (APP-148, Budget board).
 */
export function ChangedLine({
  change,
  onHistory,
  verb = 'Last changed',
  empty = 'Not changed since setup',
  historyLabel = 'History',
  className,
}: {
  change: {
    by: { name: string } | null;
    at: string;
    /** APP-148: the Executive the change was made for. */
    for?: { name: string } | null;
    /** What changed, in brackets after the date ("Terrestrial top-up"). */
    what?: string;
  } | null;
  onHistory?: () => void;
  verb?: string;
  /** What's true with no change to show ("Not set for 2026 yet", "No entries yet for FY2026"). */
  empty?: string;
  /** The link's words when it isn't the History drawer ("Budget ledger"). */
  historyLabel?: string;
  className?: string;
}): ReactElement {
  return (
    <p className={cx(s.changed, className)}>
      {change === null
        ? empty
        : `${verb}${change.by === null ? '' : ` by ${change.by.name}`}${
            change.for == null ? '' : ` for ${change.for.name}`
          } on ${formatDate(change.at)}${change.what === undefined ? '' : ` (${change.what})`}`}
      {onHistory !== undefined && (
        <>
          {' · '}
          <InlineLinkButton onPress={onHistory}>{historyLabel}</InlineLinkButton>
        </>
      )}
    </p>
  );
}

/** The grey pill that says who can change a card ("Executives and Finance edit"). */
export function WhoEdits({ children, canEdit }: { children: string; canEdit: boolean }) {
  return (
    <StatusPill
      tone="neutral"
      size="sm"
      label={canEdit ? children : `${children} · you can view`}
    />
  );
}

/** What a card shows when its setting isn't open to this person, or didn't load. */
export function SettingFallback({
  state,
  children,
  onDark = false,
}: {
  state: Exclude<SettingState, { status: 'ready' }>;
  children?: ReactNode;
  /** Inside a dark card. */
  onDark?: boolean;
}): ReactElement {
  return (
    <p
      className={cx(s.muted, onDark && s.mutedOnDark)}
      role={state.status === 'error' ? 'alert' : undefined}
    >
      {state.status === 'loading'
        ? 'Loading…'
        : state.status === 'error'
          ? 'This didn’t load. Reload the page to try again.'
          : (children ?? 'This setting isn’t open to your roles.')}
    </p>
  );
}

/**
 * Takes the person to a place on the page, the target of a same-page link (APP-093): scrolls it to
 * the top and puts focus on it. jsdom has no scrollIntoView; focus still moves.
 */
export function jumpTo(element: HTMLElement | null): void {
  if (element === null) return;
  if (typeof element.scrollIntoView === 'function') element.scrollIntoView({ block: 'start' });
  element.focus({ preventScroll: true });
}

/** A table's box: scrolls sideways inside its card, and is a named focusable region only then. */
export function ScrollBox({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(ref, label);
  return (
    <div ref={ref} className={s.tableWrap} {...region}>
      {children}
    </div>
  );
}
